import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles'
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { isMobile } from 'react-device-detect';

import style from './ApplyCard.scss';
import Tooltip from '../Tooltip';
import ApplicantsPerSeatPillBox from '../ApplicantsPerSeatPillBox';
import Button from '../Button';
import ZoneIcon from '../icons/Zone';
import CardCurrentSchoolIcon from '../icons/CardCurrentSchool';
import InfoIcon from '../icons/Info';
import LooIcon from '../icons/LooIcon';
import messages from '../../locale/messages';
import { withRtl } from '../hoc';
import { isMS, isK, is2K, is3K, isPK, hideGuaranteedOfferExcludedSchools, customisedIntlFormatMessage } from '../../utils/helpers';
import Star from '../icons/Star';
import analytics from '../../services/analytics';

const localMessages = defineMessages({
  removeSavedProgramFromApplication: {
    id: 'applyCard.removeSavedProgramFromApplication',
    defaultMessage: 'Remove program: {programTitle}, school: {schoolName} from application',
    description: 'Remove from application with program and school name',
  },
  schoolProgramLikelihoodToolTipApplicants: {
    id: 'schoolProgram.ToolTipApplicants',
    defaultMessage: `{number} applicants per seat last year`,
    description: 'number of applicants per seat last year',
  },
  schoolProgramAddedToApplicationPopup: {
    id: 'schoolProgram.AddedToApplicationPopup',
    defaultMessage: 'This program has been added to your application',
    description: 'Popup content when a program is added to the application.',
  },
  schoolProgramRemovedFromApplicationPopup: {
    id: 'schoolProgram.RemovedFromApplicationPopup',
    defaultMessage: 'This program has been removed from your application',
    description: 'Popup content when a program is removed from the application.',
  },
  schoolProgramSavedPopup: {
    id: 'schoolProgram.SavedPopup',
    defaultMessage: 'This program has been favorited.',
    description: 'Popup content when a program is saved.',
  },
  schoolProgramIsAddedToApplicationTitle: {
    id: 'schoolProgram.AddedToApplicationTitle',
    defaultMessage: 'IN APPLICATION',
    description: 'Program was added to application and can not be unsaved.',
  },
  schoolProgramIsAddedToApplication: {
    id: 'schoolProgram.AddedToApplication',
    defaultMessage: 'To unsave this program, first remove it from your application.',
    description: 'Program was added to application and can not be unsaved.',
  },
  schoolProgramHasScheduledAppointmentTitle: {
    id: 'schoolProgram.schoolProgramHasScheduledAppointmentTitle',
    defaultMessage: 'SCHEDULED',
    description: 'Program was added to application and can not be unsaved.',
  },
  schoolProgramHasScheduledAppointment: {
    id: 'schoolProgram.ScheduledAppointment',
    defaultMessage: 'To unsave this program, first cancel your scheduled appointment.',
    description: 'Program has a scheduled appointment and can not be unsaved',
  },
  schoolProgramSaveWithProgramSchoolDescription: {
    id: 'schoolProgram.SaveWithProgramSchoolDescription',
    defaultMessage: 'Save program: {programTitle}, for school: {schoolName}',
    description: 'Save program message',
  },
  schoolProgramUnsaveWithProgramSchoolDescription: {
    id: 'schoolProgram.UnsaveWithProgramSchoolDescription',
    defaultMessage: 'Unsave program: {programTitle}, for school: {schoolName}',
    description: 'Unsave program message',
  },
  schoolProgramSaveLoggedInUserTitle: {
    id: 'schoolProgram.saveLoggedInUserTitle',
    defaultMessage: `Select a child's application to add to your favorites`,
    description:
      'String title used to inform user to select a child application when saving a program in global directory',
  },
  schoolProgramSaveLoggedInUserDescription: {
    id: 'schoolProgram.saveLoggedInUserDescription',
    defaultMessage: `You can favorite programs by going to the dashboard and clicking "Get Started" or "Continue" or "Submitted".`,
    description:
      'String description used to inform user to select a child application when saving a program in global directory',
  },
  schoolProgramSaveLoggedInUserWaitlistNoResultDescription: {
    id: 'schoolProgram.saveLoggedInUserWaitlistNoResultDescription',
    defaultMessage: `You can favorite programs by going to the dashboard and clicking "Search & Join Waitlists".`,
    description:
      'String description used to inform user to select a child application when saving a program in global directory',
  },
  schoolProgramSaveLoggedInUserWaitlistResultDescription: {
    id: 'schoolProgram.saveLoggedInUserWaitlistResultDescription',
    defaultMessage: `You can favorite programs by going to the dashboard and clicking "View Results & Waitlists".`,
    description:
      'String description used to inform user to select a child application when saving a program in global directory',
  },
  schoolProgramSaveLoggedInUserClosedProgramDescription: {
    id: 'schoolProgram.saveLoggedInUserClosedProgramDescription',
    defaultMessage: `You can favorite programs when the application is open.`,
    description:
      'String description used to inform user to select a child application when saving a program in global directory',
  },
  schoolProgramSaveUnloggedInUserTitle: {
    id: 'schoolProgram.saveUnloggedInUserTitle',
    defaultMessage: 'Sign up to add programs to your favorites',
    description:
      'String title used to inform user to select a child application when saving a program in global directory',
  },
  schoolProgramSaveUnloggedInUserDescription: {
    id: 'schoolProgram.saveUnloggedInUserDescription',
    defaultMessage: 'You can favorite programs when the application is open and you are logged into your account.',
    description:
      'String description used to inform user to select a child application when saving a program in global directory',
  },
  schoolProgramAddedToYourApplication: {
    id: 'schoolProgram.AddedToYourApplication',
    defaultMessage: 'Added to your application',
    description: 'String description if program was already added to the application',
  },
  schoolProgramLoggedInModalTitle: {
    id: 'schoolProgram.LoggedInModalTitle',
    defaultMessage: `Select a child's application`,
    description: 'String representing logged in modal when saving a program in global directory',
  },
  schoolProgramCurrentSchoolProgramK: {
    id: 'schoolProgram.CurrentSchoolProgramK',
    defaultMessage: `This is your child’s current school. Your child will receive priority to this program as a current student.`,
    description: 'Kindergarten current school program tooltip',
  },
  schoolProgramCurrentSchoolProgramMessageFirstParagraph: {
    id: 'schoolProgram.CurrentSchoolProgramMessageFirstParagraph',
    defaultMessage: `By applying to this program, {firstName} will be guaranteed a seat, because this school guarantees offers to current students.`,
    description:
      'String description to tell student they are guaranteed an offer by applying to this program, first paragraph',
    values: {
      firstName: 'your child',
    },
  },
  schoolProgramCurrentSchoolProgramMessageSecondParagraph: {
    id: 'schoolProgram.CurrentSchoolProgramMessageSecondParagraph',
    defaultMessage: `Put it below other programs on your application list -- otherwise {firstName} will match to this program and not be considered for ones listed below it.`,
    description:
      'String description to tell student they are guaranteed an offer by applying to this program, second paragraph',
    values: {
      firstName: 'your child',
    },
  },
  schoolProgramKindergartenEligibleTooltipBody: {
    id: 'schoolProgram.KindergartenEligibleTooltipBody',
    defaultMessage:
      'Your chances of an offer are a combination of your priority group for this program and its offers last year. Open the program’s details to review its priority groups.',
    description: 'Kindergarten student eligibility tooltip message',
  },
});

class ApplyCard extends PureComponent {
  static propTypes = {
    distance: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    hideButton: PropTypes.bool,
    id: PropTypes.number.isRequired,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    removeProgram: PropTypes.func,
    // schoolName: PropTypes.string.isRequired,
    directory_program: PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      is_current_school: PropTypes.bool.isRequired,
      is_zoned_program: PropTypes.bool.isRequired,
      directory_school_id: PropTypes.number.isRequired,
      directory_school_name: PropTypes.string.isRequired,
      directory_student_program: PropTypes.arrayOf(
        PropTypes.shape({
          priority_group: PropTypes.shape({
            order: PropTypes.number,
            description: PropTypes.string,
          }),
        }),
      ).isRequired,
      directory_priority_groups: PropTypes.array.isRequired,
      demand_last_year: PropTypes.shape({
        general_education: PropTypes.shape({
          applications_per_seat: PropTypes.number,
        }).isRequired,
      }).isRequired,
    }).isRequired,
    intl: PropTypes.shape().isRequired,
    user: PropTypes.shape({}).isRequired,
  };
  static defaultProps = {
    hideButton: false,
    distance: 'not in api',
    onClick: () => {},
    onKeyDown: undefined,
    removeProgram: () => {},
  };

  currentSchoolTooltipBodyK = () => {
    const { intl } = this.props;

    return (
      <div className={style.tip}>
        <Tooltip
          icon={<CardCurrentSchoolIcon fill="#024cbc" size="15" />}
          ariaLabel={customisedIntlFormatMessage(intl, messages.schoolProgramCurrentSchoolInfo, {
            programTitle: this.props.directory_program.name,
            schoolName: this.props.directory_program.directory_school_name,
          })}
        >
          <p>
            <FormattedMessage {...localMessages.schoolProgramCurrentSchoolProgramK} />
          </p>
        </Tooltip>
      </div>
    );
  };

  currentSchoolTooltipBody = () => {
    const { firstName, intl } = this.props;
    if (!hideGuaranteedOfferExcludedSchools(this.props.school.dbn)) {
      return (
        <div className={style.tip}>
          <Tooltip
            icon={<CardCurrentSchoolIcon fill="#024cbc" size="15" />}
            ariaLabel={customisedIntlFormatMessage(intl, messages.schoolProgramCurrentSchoolInfo, {
              programTitle: this.props.directory_program.name,
              schoolName: this.props.directory_program.directory_school_name,
            })}
          >
            <p>
              <FormattedMessage
                {...localMessages.schoolProgramCurrentSchoolProgramMessageFirstParagraph}
                values={{ firstName }}
              />
            </p>
            <p>
              <i>
                <FormattedMessage
                  {...localMessages.schoolProgramCurrentSchoolProgramMessageSecondParagraph}
                  values={{ firstName }}
                />
              </i>
            </p>
          </Tooltip>
        </div>
      );
    }
    return null;
  };

  zonedProgramTooltipBody = () => {
    const { firstName, intl, user } = this.props;

    let tooltipMessage = <FormattedMessage {...messages.schoolProgramToolTipZoned} values={{ firstName }} />;
    if (isMS({ user })) {
      tooltipMessage = <FormattedMessage {...messages.schoolProgramToolTipZonedMiddleSchool} values={{ firstName }} />;
    } else if (isK({ user }) || isPK({ user }) || is2K({ user }) || is3K({ user })) {
      tooltipMessage = <FormattedMessage {...messages.schoolProgramToolTipZonedK} />;
    }

    return (
      <div className={style.tip}>
        <Tooltip
          icon={<ZoneIcon border="#000" fill="#4EE4B3" size="15" />}
          ariaLabel={customisedIntlFormatMessage(intl, messages.schoolProgramZonedProgramInfo, {
            programTitle: this.props.directory_program.name,
            schoolName: this.props.directory_program.directory_school_name,
          })}
        >
          <p>{tooltipMessage}</p>
        </Tooltip>
      </div>
    );
  };

  eligibleKindergartenTooltipBody = () => {
    const { intl } = this.props;
    return (
      <div className={style.tip} data-coachmark="search-step-7">
        <Tooltip
          icon={<InfoIcon border="#000" fill="#4DB6F7" size="15" />}
          ariaLabel={customisedIntlFormatMessage(intl, messages.schoolProgramEligibleInfo, {
            programTitle: this.props.directory_program.name,
            schoolName: this.props.directory_program.directory_school_name,
          })}
        >
          <div className={style.messaging}>
            <FormattedMessage {...localMessages.schoolProgramKindergartenEligibleTooltipBody} />
          </div>
        </Tooltip>
      </div>
    );
  };

  eligibleTooltipBody = () => {
    const { intl } = this.props;
    const applicantsPerSeat = this.props.directory_program.demand_last_year.general_education.applications_per_seat;
    return (
      <div className={style.tip} data-coachmark="search-step-7">
        <Tooltip
          icon={<InfoIcon border="#000" fill="#4DB6F7" size="15" />}
          ariaLabel={customisedIntlFormatMessage(intl, messages.schoolProgramEligibleInfo, {
            programTitle: this.props.directory_program.name,
            schoolName: this.props.directory_program.directory_school_name,
          })}
        >
          <h6>
            <FormattedMessage {...messages.schoolProgramToolTipTopTitle} />
          </h6>
          <div className={style.eligibilityTipTopParagraph}>
            {customisedIntlFormatMessage(intl, messages.schoolProgramToolTipTopParagraph)}
          </div>
          {applicantsPerSeat > 0 && (
            <div className={style.statsBox}>
              <div className={style.statsBoxTitle}>
                {' '}
                <FormattedMessage {...messages.generalStringApplicantsPerSeat} />
              </div>
              <ApplicantsPerSeatPillBox applicantsPerSeat={applicantsPerSeat} />
              <div className={style.noUnderline}>
                {applicantsPerSeat} <FormattedMessage {...messages.generalStringApplicantsPerSeatLastYear} />
              </div>
            </div>
          )}
          {this.renderPriorityGroup()}
          <div className={style.messaging}>
            <FormattedMessage {...messages.schoolProgramToolTipLikelihoodFirstParagraph} />
          </div>
        </Tooltip>
      </div>
    );
  };

  renderEligiblityTip = () => {
    const { user } = this.props;
    if (this.props.directory_program.is_zoned_program) {
      return this.zonedProgramTooltipBody();
    } else if (this.props.directory_program.is_current_school) {
      if (isK({ user }) || isPK({ user }) || is2K({ user }) || is3K({ user })) {
        return this.currentSchoolTooltipBodyK();
      }
      return this.currentSchoolTooltipBody();
    }
    if (isK({ user }) || isPK({ user }) || is2K({ user }) || is3K({ user })) {
      return this.eligibleKindergartenTooltipBody();
    }
    return this.eligibleTooltipBody();
  };

  renderPriorityGroup = () => {
    const directoryProgram = this.props.directory_program;
    if (!directoryProgram.directory_student_program.length) {
      return null;
    }

    const all = directoryProgram.directory_priority_groups.filter(priority => priority.name);

    if (directoryProgram.directory_student_program[0].priority_group) {
      const studentPriorityGroup = directoryProgram.directory_student_program[0].priority_group;
      const forStudent = studentPriorityGroup.order;
      let studentPriorityGroupName = studentPriorityGroup.name;
      let studentPriorityGroupDescription = studentPriorityGroup.ge_priority_group_description;
      if (this.props.user.is_student_swd){
        studentPriorityGroupDescription = studentPriorityGroup.swd_priority_group_description
      }
      // const { all, forStudent, studentPriorityGroupDescription } = getPriorityGroups();

      const currentPriority = all.map(priority => {
        if (priority.order === forStudent) {
          return (
            <span key={`priority-${priority.id}`} className={style.activePriorityNumberToolTip}>
              {priority.order}
            </span>
          );
        }
        return (
          <span key={`priority-${priority.id}`} className={style.priorityNumberToolTip}>
            {priority.order}
          </span>
        );
      });
      return (
        <div className={style.statsBox}>
          <div className={style.statsBoxTitle}>
            <FormattedMessage {...messages.generalStringToolTipPriorityGroup} />
          </div>
          <div>{currentPriority}</div>
          <div>{studentPriorityGroupName}</div>
          <div>{studentPriorityGroupDescription}</div>
        </div>
      );
    }
    return null;
  };

  looTooltipContent = () => {
    const { directory_program } = this.props;
    const { admissions_method, loo_chances } = directory_program;
    let loo_data = null;
    if(loo_chances == "HIGH_PROB"){
      loo_data = this.props.user.loo_indicator_tooltip_content_high_prob;
    }else if(loo_chances == "MEDIUM_PROB"){
      loo_data = this.props.user.loo_indicator_tooltip_content_medium_prob;
    }else if(loo_chances == "LOW_PROB"){
      loo_data = this.props.user.loo_indicator_tooltip_content_low_prob;
    } else {
      loo_data = this.props.user.loo_indicator_tooltip_content_no_prob;
    }
    if(admissions_method.loo_contents.length > 0){
      admissions_method.loo_contents.forEach(function(obj){
        if(obj.loo_prob == loo_chances){
          loo_data = obj.content
        }
      })
    }
    return loo_data;
  }

  handleLooAnalyticsMouseEnter = () => {
    const { program, directory_school_dbn, directory_school_name } = this.props.directory_program;
    const { code, name } = program;
    let dataDictionary = {
      programName: `${name}`,
      programCode: `${code}`,
      schoolName: `${directory_school_name}`,
      schoolDbn: `${directory_school_dbn}`,
      loggedIn: true
    };
    if(this.props.user.show_loo_tooltip){
      const { loo_chances } = this.props.directory_program;
      dataDictionary['looIndicator'] = loo_chances || 'NOT_AVAIL_PROB';
    }
    analytics({
      eventLabel: 'looTooltip',
      dataDictionary: dataDictionary,
    });
  }

  looTooltipBody = () => {
    const directoryProgram = this.props.directory_program;
    const { intl } = this.props;
    const { name, directory_school_name, loo_chances } = directoryProgram;
    if (!this.props.user.show_loo_tooltip) {
      return null;
    }
    let looTootTipWidth = 275;
    if(isMobile){
      looTootTipWidth = 200;
    }
    let loo_data = this.looTooltipContent();

    return (
      <div className={style.tipLoo} data-coachmark="search-step-7">
        <Tooltip
          icon={<LooIcon loo_chances={loo_chances} height='29.122' width='34.306'/>}
          ariaLabel={customisedIntlFormatMessage(intl, messages.schoolProgramEligibleInfo, {
            programTitle: name,
            schoolName: directory_school_name,
          })}
          applyMobileWidth={looTootTipWidth}
          keepTooltipOpen={true}
          onMouseEnter={this.handleLooAnalyticsMouseEnter}
        >
          <div> <LooIcon loo_chances={loo_chances} height='59.244' width='68.612' /> </div>
          <div>
            <p className={style.looText} dangerouslySetInnerHTML={{ __html: loo_data }} />
          </div>
          {this.renderPriorityGroup()}
        </Tooltip>
      </div>
    );
  };

  render() {
    const directoryProgram = this.props.directory_program;
    const { id, intl, onClick, onKeyDown } = this.props;
    const { name } = directoryProgram;
    return (
      <div className={style.root}>
        <div
          className={style.schoolContainer}
          onKeyDown={onKeyDown || onClick}
          role="link"
          tabIndex="0"
          onClick={onClick}
        >
          <div className={style.schoolNameContainer}>
            <h3 className={style.applyCardHeader}>
              <span>{directoryProgram.directory_school_name}</span>
            </h3>
          </div>
          <div className={style.secondRowContainer}>
            <div className={style.starContainer}>
              <Star fill="#4EE4B3" size="16px" />
            </div>
            <div className={style.applyCardProgram}>
              <span>{name}</span>
              {this.looTooltipBody()}
            </div>
          </div>
        </div>
        {!this.props.hideButton && (
          <div className={style.button}>
            <Button
              customClass="program-remove"
              blueFont
              full
              label={customisedIntlFormatMessage(intl, messages.generalStringRemoveFromApplication)}
              ariaLabel={customisedIntlFormatMessage(intl, localMessages.removeSavedProgramFromApplication, {
                programTitle: name,
                schoolName: directoryProgram.directory_school_name,
              })}
              onClick={() => {
                this.props.removeProgram(directoryProgram.id, directoryProgram.directory_school_id, true, id);
              }}
            >
              <i className="icon-Remove program-remove" role="presentation" />
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default withRtl(style)(injectIntl(withStyles(style)(ApplyCard)));
